import type { RV2ComplianceApplication } from '../number-compliance-v2/types-v2'
import {
  AGGREGATOR_TM_DECLARATION,
  BFSI_CAF,
  DOWNLOAD_FORM_URLS,
  GST_CERT_PRINCIPAL_ENTITY,
  GST_CERT_TELEMARKETER,
  NUMBER_SERIES_ALIAS_PREFIX,
} from './constants'
import { BUSINESS_NAME_FIELD, isGstDocument } from './gst-documents'
import type { CallsPlacedType, NumberSeries } from './types'

// Get country name from ISO2 code
export const getCountryName = (iso2: string): string => {
  const countryData = window.contactoCountryData?.find(
    (country) => Array.isArray(country) && country[1] === iso2,
  )
  return countryData?.[0] ? countryData[0] : iso2
}

/**
 * Utility functions for generating consistent document field keys
 * Used for error keys, field IDs, and data-testid attributes
 */
export const getDocumentFieldKey = (documentTypeId: string, fieldName: string) =>
  `${documentTypeId}-${fieldName}`

export const getDocumentFileKey = (documentTypeId: string) =>
  getDocumentFieldKey(documentTypeId, 'file')

// Identifiers that title-casing would mangle ("gstin" -> "Gstin").
const META_FIELD_LABELS: Record<string, string> = {
  cin: 'CIN',
  cin_llpin: 'CIN / LLPIN',
  gstin: 'GSTIN',
  llpin: 'LLPIN',
  pan: 'PAN',
  tan: 'TAN',
}

// Reading order for the details read off a business document: the number that
// identifies the business on the certificate, then the name, then the address.
// Keys the backend sends that aren't listed keep their own order, after these.
// Both Udyam spellings are listed: resolveRegistrationDocumentType (gst-documents.ts)
// reads `udyam_registration_number`, but this list predates that and named it
// `udyam_number` — kept until it's confirmed dead rather than guessed away.
const DETAIL_FIELD_ORDER = [
  'gstin',
  'cin_llpin',
  'cin',
  'llpin',
  'udyam_number',
  'udyam_registration_number',
  'pan',
  'legal_business_name',
  'legal_name',
  'business_name',
  'registered_address',
  'address',
]

// Whichever of these the extraction used — the business's legal name is what
// the console shows as Business Name, so it has one place to be resolved.
const LEGAL_NAME_KEYS = ['legal_business_name', 'legal_name', 'business_name']

export const selectLegalNameFromDetails = (
  details: Record<string, Record<string, string>>,
): string | undefined => {
  for (const documentDetails of Object.values(details)) {
    for (const key of LEGAL_NAME_KEYS) {
      const value = documentDetails[key]
      if (value?.trim()) return value.trim()
    }
  }
  return undefined
}

/**
 * The legal name a resubmission files, resolved in priority order: the stored
 * end_user name, then the registration slot's own recorded business_name, then
 * whatever OCR read off any stored document.
 *
 * Never the alias — it is a customer-chosen nickname ("Test12"), not the
 * entity's legal name, and sending one into a regulatory filing is the bug this
 * order exists to prevent. `undefined` means none of the three settled it, and
 * the caller must block the resubmission rather than guess.
 */
export const selectResubmitLegalName = (
  application: Pick<RV2ComplianceApplication, 'end_user' | 'documents'>,
): string | undefined => {
  if (application.end_user?.name) return application.end_user.name

  const documents = application.documents ?? []
  const registrationDocument = documents.find((doc) => !isGstDocument(doc.document_name))
  const recordedBusinessName = registrationDocument?.meta_information?.[BUSINESS_NAME_FIELD]?.trim()
  if (recordedBusinessName) return recordedBusinessName

  const documentDetails: Record<string, Record<string, string>> = {}
  for (const doc of documents) {
    if (doc.meta_information) documentDetails[doc.document_type_id] = doc.meta_information
  }
  return selectLegalNameFromDetails(documentDetails)
}

export const orderDocumentDetails = <T>(entries: [string, T][]): [string, T][] => {
  const rank = (key: string) => {
    const index = DETAIL_FIELD_ORDER.indexOf(key.toLowerCase())
    return index === -1 ? DETAIL_FIELD_ORDER.length : index
  }
  return (
    entries
      .map((entry, index) => ({ entry, index }))
      // Index breaks ties so unranked keys stay in the order the backend sent them.
      .sort((a, b) => rank(a.entry[0]) - rank(b.entry[0]) || a.index - b.index)
      .map(({ entry }) => entry)
  )
}

// Document meta_information arrives snake_cased from the backend; render it as
// a readable label ("legal_business_name" -> "Legal Business Name").
export const formatMetaFieldName = (key: string): string =>
  META_FIELD_LABELS[key.toLowerCase()] ??
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const convertMBToBytes = (mbString: string): number => {
  const mb = parseInt(mbString.split(' ')[0], 10)
  if (Number.isNaN(mb) || mb < 0) {
    return 0
  }
  return mb * 1024 * 1024 // Convert MB to bytes
}

// --- 140/160 number series (display-layer only, backend unchanged) ---

export const getNumberSeriesFromAlias = (alias: string): NumberSeries => {
  if (alias.startsWith(NUMBER_SERIES_ALIAS_PREFIX['140'])) return '140'
  if (alias.startsWith(NUMBER_SERIES_ALIAS_PREFIX['160'])) return '160'
  return 'other'
}

// The series a chosen compliance pins its numbers to, gated on the 140/160 flag:
// the alias-derived series when enabled, otherwise undefined (no series coupling).
export const seriesForComplianceAlias = (
  alias: string,
  enabled: boolean,
): NumberSeries | undefined => (enabled ? getNumberSeriesFromAlias(alias) : undefined)

export const stripNumberSeriesPrefix = (alias: string): string =>
  alias.slice(NUMBER_SERIES_ALIAS_PREFIX[getNumberSeriesFromAlias(alias)].length)

const ALIAS_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'

/**
 * The CAF update's alias, e.g. `k3j9x2ab-CAF`. That dialog has no Name field, so
 * nobody types one; the suffix marks the application as a CAF update.
 */
export const generateCafUpdateAlias = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  const id = Array.from(bytes, (byte) => ALIAS_ALPHABET[byte % ALIAS_ALPHABET.length]).join('')
  return `${id}-CAF`
}

export interface SeriesDocOverride {
  displayName: string
  downloadFormUrl?: string
}

const docOverride = (displayName: string): SeriesDocOverride => ({
  displayName,
  downloadFormUrl: DOWNLOAD_FORM_URLS[displayName],
})

// Maps the existing India document slots (keyed by API document_name) to the
// 140/160 series document labels. Both slots always render and collect a file —
// the API requires two documents per application — so the series only changes
// display text and download links. A null return keeps the default label.
export const getSeriesDocOverride = (
  series: NumberSeries,
  callsPlacedType: CallsPlacedType | null,
  documentName: string,
): SeriesDocOverride | null => {
  if (series === 'other') return null

  const isRegCertSlot = documentName === 'Registration Certificate'
  const isGstSlot = documentName.toUpperCase().includes('GST')

  if (series === '140') {
    if (isRegCertSlot) {
      return docOverride(AGGREGATOR_TM_DECLARATION)
    }
    // Second document is the telemarketer's GST certificate.
    if (isGstSlot) {
      return docOverride(GST_CERT_TELEMARKETER)
    }
  }

  if (series === '160') {
    if (isRegCertSlot) {
      return docOverride(BFSI_CAF)
    }
    if (isGstSlot) {
      // Partner-managed: aggregator declaration. Self-managed: the principal
      // entity's GST certificate. (Before calls-placed is chosen, default label.)
      if (callsPlacedType === 'partner') {
        return docOverride(AGGREGATOR_TM_DECLARATION)
      }
      if (callsPlacedType === 'self') {
        return docOverride(GST_CERT_PRINCIPAL_ENTITY)
      }
    }
  }

  return null
}

// Single source for the label a document slot renders under: series override
// first, then the GST -> Address Proof rename, then the API name. Keeps card
// titles and validation error messages in sync.
export const getDocumentDisplayName = (
  series: NumberSeries,
  callsPlacedType: CallsPlacedType | null,
  documentName: string,
): string =>
  getSeriesDocOverride(series, callsPlacedType, documentName)?.displayName ??
  (documentName.toUpperCase().includes('GST') ? 'Address Proof' : documentName)
